import { useState, useEffect, useCallback, useContext } from 'react';
import monthlyReportService from '../../auditor/service/monthlyReportService.mjs';
import { AuthContext } from '../../authProvider.mjs';

/**
 * Hook for director dashboard functionality
 * Handles both regional and global director views
 */
export const useDirectorDashboard = (directorType = 'regional') => {
  const [pendingReports, setPendingReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // actionLoading is still reported so a consumer can disable controls during a
  // refetch; nothing sets it now that the write actions have moved out.
  const [actionLoading] = useState(false);

  const { user, isRegionalDirector, isGlobalDirector, isFinanceDirector } = useContext(AuthContext);

  // Fetch pending reports for approval
  const fetchPendingReports = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let result;
      if (directorType === 'global' || isGlobalDirector || isFinanceDirector) {
        result = await monthlyReportService.getGlobalDirectorDashboard();
      } else {
        result = await monthlyReportService.getRegionalDirectorDashboard();
      }

      setPendingReports(result.data || []);
    } catch (err) {
      setError(err.message);
      setPendingReports([]);
    } finally {
      setLoading(false);
    }
  }, [directorType, isGlobalDirector, isFinanceDirector]);

  // Approving and sending back are NOT here. Both dashboards are view-only:
  // signing off a month's spend from a table row means doing it without having
  // opened the claims. The report page owns those actions (useMonthlyReport).

  // Get approval chain for a report
  const getApprovalChain = useCallback(async (reportId) => {
    try {
      const result = await monthlyReportService.getApprovalChain(reportId);
      return result.data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Verify report integrity
  const verifyIntegrity = useCallback(async (reportId) => {
    try {
      const result = await monthlyReportService.verifyIntegrity(reportId);
      return result.data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchPendingReports();
  }, [fetchPendingReports]);

  // Separate pending and approved reports based on director type
  const getPendingStatuses = () => {
    if (directorType === 'global' || isGlobalDirector || isFinanceDirector) {
      // 'auditor_approved' too. The server only sends one to a global or
      // finance director when it is waiting on them - a report that skips the
      // regional stage, or one they were named to decide - and this list used
      // to drop exactly those, so a skip-regional report never showed as
      // pending for the person who had to sign it.
      return ['regional_director_approved', 'auditor_approved'];
    }
    return ['submitted', 'auditor_approved']; // Reports waiting for regional approval
  };

  const getApprovedStatuses = () => {
    if (directorType === 'global' || isGlobalDirector || isFinanceDirector) {
      return ['global_director_approved'];
    }
    return ['regional_director_approved', 'global_director_approved'];
  };

  const pendingStatuses = getPendingStatuses();
  const approvedStatuses = getApprovedStatuses();

  // `canAct` is the server's verdict (reviewCapacity) for this reader. It puts a
  // report waiting on them in the pending list whatever its status - a
  // regional director covering final approval by delegation, or a global
  // director stepping into a regional stage nobody is left to sign
  // (2026-10-22) - where status alone filed it as already approved.
  const actualPendingReports = pendingReports.filter(r => r.canAct === true || pendingStatuses.includes(r.status));
  const approvedReports = pendingReports.filter(r => r.canAct !== true && approvedStatuses.includes(r.status));
  // Sent back after reaching this director. They fell into neither list above,
  // so a report vanished from the dashboard the moment it was returned.
  const sentBackReports = pendingReports.filter(r => r.status === 'rejected');

  // Stats calculations - only count actually pending reports
  const stats = {
    totalPending: actualPendingReports.length,
    totalApproved: approvedReports.length,
    totalSentBack: sentBackReports.length,
    // parseFloat: totalAmount arrives as a DECIMAL string, and string
    // concatenation here rendered the dashboard total as NaN.
    totalAmount: actualPendingReports.reduce((sum, r) => sum + (parseFloat(r.totalAmount) || 0), 0),
    approvedAmount: approvedReports.reduce((sum, r) => sum + (parseFloat(r.totalAmount) || 0), 0),
    byRegion: actualPendingReports.reduce((acc, r) => {
      const region = r.region || 'Unknown';
      acc[region] = (acc[region] || 0) + 1;
      return acc;
    }, {})
  };

  return {
    pendingReports: actualPendingReports,
    approvedReports,
    sentBackReports,
    allReports: pendingReports,
    loading,
    error,
    actionLoading,
    stats,
    user,
    isRegionalDirector,
    isGlobalDirector,
    isFinanceDirector,
    fetchPendingReports,
    getApprovalChain,
    verifyIntegrity,
    setError
  };
};

export default useDirectorDashboard;
